import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ArrowLeft } from 'lucide-react';
import Header from '../components/Header';

export default function TermsOfUse() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-background">
      <Header />

      <div className="pt-32 pb-20 px-6">
        <div className="max-w-4xl mx-auto">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/')}
            className="mb-8"
            data-testid="back-button"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Home
          </Button>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-card border border-border rounded-lg p-8"
          >
            <h1 className="text-4xl font-heading font-bold mb-4">Terms of Use</h1>
            <p className="text-sm text-muted-foreground mb-8">
              <strong>Effective Date:</strong> January 5, 2026
            </p>

            <div className="prose prose-invert max-w-none space-y-6 text-foreground">
              <p>
                Welcome to <strong>MyTestCase</strong>. By accessing or using our website and services, you agree to comply with and be bound by the following terms and conditions.
              </p>

              <div>
                <h2 className="text-2xl font-heading font-semibold mb-3 mt-8">1. Acceptance of Terms</h2>
                <p>
                  By using{' '}
                  <a href="https://mytestcase.com" className="text-blue-500 hover:underline">
                    https://mytestcase.com
                  </a>{' '}
                  and its associated preview subdomains, you agree to these Terms of Use, our Privacy Policy, and all applicable laws and regulations. If you do not agree, you are prohibited from using the site.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-heading font-semibold mb-3 mt-8">2. Use of AI Services</h2>
                <ul className="list-disc pl-6 space-y-2">
                  <li>
                    <strong>AI-Generated Content:</strong> <strong>MyTestCase</strong> uses artificial intelligence to generate test cases. You acknowledge that AI-generated output may contain errors, bugs, or inaccuracies.
                  </li>
                  <li>
                    <strong>User Responsibility:</strong> You are solely responsible for reviewing and validating any code or test cases generated by our platform before implementing them in a production environment.
                  </li>
                  <li>
                    <strong>No Warranty:</strong> We do not guarantee that the generated tests will identify all software defects or satisfy specific compliance requirements.
                  </li>
                </ul>
              </div>

              <div>
                <h2 className="text-2xl font-heading font-semibold mb-3 mt-8">3. Intellectual Property</h2>
                <ul className="list-disc pl-6 space-y-2">
                  <li>
                    <strong>Platform Rights:</strong> All software, design, and branding on this site are the intellectual property of <strong>MyTestCase</strong> and its creators.
                  </li>
                  <li>
                    <strong>Generated Output:</strong> We grant you a non-exclusive, royalty-free license to use, modify, and distribute the specific test cases and scripts generated by you.
                  </li>
                </ul>
              </div>

              <div>
                <h2 className="text-2xl font-heading font-semibold mb-3 mt-8">4. User Conduct and Restrictions</h2>
                <p>You agree <strong>not</strong> to:</p>
                <ul className="list-disc pl-6 space-y-2 mt-2">
                  <li>Use the service for any illegal or unauthorized purpose.</li>
                  <li>Attempt to reverse-engineer the AI models or platform infrastructure.</li>
                  <li>Input sensitive or confidential production data into the AI prompt unless you have the right to do so.</li>
                  <li>Abuse the service by using automated tools to "burn" credits or bypass security features.</li>
                </ul>
              </div>

              <div>
                <h2 className="text-2xl font-heading font-semibold mb-3 mt-8">5. Disclaimer of Liability</h2>
                <p>
                  <strong>MyTestCase</strong> is provided on an "as-is" and "as-available" basis. We shall not be liable for any direct, indirect, or incidental damages resulting from:
                </p>
                <ul className="list-disc pl-6 space-y-2 mt-2">
                  <li>The use or inability to use our AI agents.</li>
                  <li>Errors in AI-generated scripts that lead to software failure or data loss.</li>
                  <li>Service interruptions or "Invalid Domain" errors during preview phases.</li>
                </ul>
              </div>

              <div>
                <h2 className="text-2xl font-heading font-semibold mb-3 mt-8">6. Changes to Terms</h2>
                <p>
                  We reserve the right to modify these terms at any time. Continued use of the site following any changes constitutes your acceptance of the new terms.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
}
